import cx from 'classnames';
import { ReactNode, forwardRef, useEffect, useRef } from 'react';
import Color, { TextColors } from '../../utilities/Color.js';

export type Tag = 'h1' | 'h2' | 'h3' | 'h4' | 'p' | 'span' | 'dt' | 'dd' | 'div' | 'li';
export type Variant =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'body'
  | 'fieldLabel'
  | 'label'
  | 'largeBody'
  | 'stat'
  | 'statusBlockHeader';
export type Alignment = 'left' | 'center' | 'right';
type TextTransform = 'normal-case' | 'uppercase' | 'lowercase' | 'capitalize';
enum TextTransformEnum {
  'normal-case' = 'normal-case',
  uppercase = 'uppercase',
  lowercase = 'lowercase',
  capitalize = 'capitalize',
}

const variantToTag: { [variant in Variant]: Tag } = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  statusBlockHeader: 'h3',
  largeBody: 'p',
  body: 'p',
  label: 'p',
  fieldLabel: 'span',
  stat: 'span',
};

export interface TypographyProps {
  alignment?: Alignment;
  bold?: boolean;
  children: ReactNode;
  color?: Color;
  id?: string;
  tag?: Tag;
  textTransform?: TextTransform;
  underline?: boolean;
  variant: Variant;
}

const Typography = forwardRef(function InnerTypography(
  { alignment, bold, children, color, id, tag, textTransform, underline, variant }: TypographyProps,
  ref,
): JSX.Element {
  const isHeading = !['body', 'largeBody', 'label', 'fieldLabel', 'stat'].includes(variant);
  let textTransformOverride: TextTransform;
  const Tag = tag ?? variantToTag[variant];

  if (variant === 'label') {
    textTransformOverride = 'capitalize';
  }

  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => heading.current?.focus(), []);

  return (
    <Tag
      id={id}
      // @ts-ignore not sure how to type this since Tag is dynamic
      ref={ref || heading}
      className={cx(
        color && TextColors[color],
        (textTransformOverride || textTransform) &&
          TextTransformEnum[textTransformOverride || textTransform],
        'font-sans',
        {
          'text-4xl lg:text-6xl leading-9 lg:leading-11 font-semibold lg:font-medium focus:outline-none':
            variant === 'h1',
          'lg:text-6xl text-5xl leading-8 lg:leading-10': variant === 'stat',
          'text-2xl lg:text-5xl leading-7 lg:leading-10 font-semibold lg:font-medium':
            variant === 'h2',
          'text-lg lg:text-xl leading-6.5 lg:leading-7': variant === 'h3',
          'text-lg leading-5 font-semibold': variant === 'h4',
          'text-2xl lg:text-3xl leading-6.5 lg:leading-7 font-semibold':
            variant === 'statusBlockHeader',
          'font-medium': variant === 'h3' && !bold,
          'text-xl leading-6.5 lg:leading-7': variant === 'largeBody',
          'text-base leading-5': variant === 'body',
          'text-sm leading-5': variant === 'label',
          'text-xs leading-4 lg:leading-5 tracking-tight': variant === 'fieldLabel',
          'font-normal': !isHeading && !bold,
          'font-bold': bold,
          underline,
          'text-center': alignment === 'center',
          'text-left': alignment === 'left',
          'text-right': alignment === 'right',
        },
      )}
      tabIndex={variant === 'h1' ? -1 : undefined}
    >
      {children}
    </Tag>
  );
});
export default Typography;
